import React from 'react';
import { useTranslation } from 'react-i18next';
import { MAP_SEARCH_QUERY_MIN_LENGTH } from './constants';
import './SearchPanel.css';

/** Ikona lupy w stylu aplikacji (obrys + currentColor z przycisku) */
function MapSearchMagnifierIcon({ className }) {
  return (
    <span className={className}>
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="10.25" cy="10.25" r="5.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path
          d="M14.5 14.5 20 20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="10.25" cy="10.25" r="2" fill="currentColor" fillOpacity="0.12" stroke="none" />
      </svg>
    </span>
  );
}

/** Zamknięcie — zaokrąglony X jak w reszcie UI */
function MapSearchCloseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M8.5 8.5 15.5 15.5M15.5 8.5 8.5 15.5"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchPanel({
  searchOpen,
  onClose,
  searchAddress,
  onAddressChange,
  onSearch,
  searchSuggestions,
  onSuggestionClick,
  searchRange,
  onRangeChange,
  searching,
  searchLocationActive = false,
}) {
  const { t, i18n } = useTranslation(['discover', 'shared']);
  const radiusDisplay = new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(Number(searchRange));

  const trimmedQuery = (searchAddress || '').trim();
  const queryTooShort =
    trimmedQuery.length > 0 && trimmedQuery.length < MAP_SEARCH_QUERY_MIN_LENGTH;
  const canSubmitSearch =
    trimmedQuery.length >= MAP_SEARCH_QUERY_MIN_LENGTH && !searching;

  return (
    <>
      <button
        className={`map-search-floating-button ${searchOpen ? 'active' : ''}`}
        onClick={() => onClose(!searchOpen)}
        type="button"
        title={t('discover.map.search', 'Szukaj')}
        aria-expanded={searchOpen}
        aria-controls="map-search-panel"
      >
        <MapSearchMagnifierIcon className="map-search-floating-icon" />
      </button>

      <div
        className={`map-search-overlay ${searchOpen ? 'open' : ''}`}
        id="map-search-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="map-search-panel-title"
        aria-hidden={!searchOpen}
      >
        <div className="map-search-container">
          <div className="map-search-header">
            <h3 className="map-search-title" id="map-search-panel-title">
              {t('discover.map.search', 'Szukaj')}
            </h3>
            <button
              className="map-search-close-button"
              onClick={() => onClose(false)}
              type="button"
              aria-label={t('shared:common.close')}
            >
              <MapSearchCloseIcon className="map-search-close-icon" />
            </button>
          </div>
          {searchOpen && searchLocationActive && (
            <p className="map-search-active-filter" role="status" aria-live="polite">
              {t(
                'discover.map.activeSearchFilter',
                'Na mapie pokazywane są tylko wydarzenia w okolicy {{place}} w promieniu {{radius}} km (obszar okręgu).',
                { place: searchAddress || t('discover.map.searchUnknownPlace', 'wybrana lokalizacja'), radius: radiusDisplay }
              )}
            </p>
          )}
          <div className="map-search-input-group">
            <input
              type="text"
              className="map-search-input"
              placeholder={t('discover.map.searchAddress', 'Wpisz adres lub miasto...')}
              value={searchAddress}
              onChange={(e) => onAddressChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmitSearch) {
                  onSearch(null, searchAddress);
                }
              }}
              aria-invalid={queryTooShort}
              aria-describedby={queryTooShort ? 'map-search-query-hint' : undefined}
            />
            {queryTooShort && (
              <p id="map-search-query-hint" className="map-search-query-hint">
                {t('discover.map.searchQueryHint', {
                  min: MAP_SEARCH_QUERY_MIN_LENGTH,
                  defaultValue: 'Minimum {{min}} znaków albo wybierz podpowiedź z listy.',
                })}
              </p>
            )}
            {searchSuggestions.length > 0 && (
              <div className="map-search-suggestions">
                {searchSuggestions.map((prediction, idx) => (
                  <button
                    key={`${prediction.place_id}-${idx}`}
                    type="button"
                    className="map-search-suggestion-item"
                    onClick={() => {
                      onSuggestionClick(prediction);
                    }}
                  >
                    {prediction.description}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="map-search-range-group">
            <label className="map-search-range-label" htmlFor="map-search-range-input">
              {t('discover.map.range', 'Zasięg')}:
            </label>
            <input
              id="map-search-range-input"
              type="number"
              className="map-search-range-input"
              min="1"
              max="50"
              value={searchRange}
              onChange={(e) => onRangeChange(parseInt(e.target.value, 10) || 5)}
            />
            <span className="map-search-range-unit">km</span>
          </div>
          <button
            className="map-search-button"
            onClick={() => {
              onSearch(null, searchAddress);
            }}
            disabled={!canSubmitSearch}
            type="button"
          >
            {searching ? t('discover.map.searching', 'Szukam...') : t('discover.map.search', 'Szukaj')}
          </button>
        </div>
      </div>
    </>
  );
}

export default SearchPanel;
